import React, { useEffect, useState } from 'react';
import { ColumnsType } from 'antd/es/table';
import { productType, initProductType } from '../../../model/master';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { api_product_type } from '../../../api/product';
import CommonTableList from '../../common/common-table-list';
import CommonDetail from '../../common/common-detail';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { Modal } from 'antd';
import { isEmpty } from '../../../utils/util';
import ProductTypeDetail from './product-type-detail';

const ProductType: React.FC = () => {
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState('');
    const [title, setTitle] = useState('');
    const [data, setData] = useState<productType[]>([]);
    const [formData, setFormData] = useState<productType>(initProductType);

    const filterField: any = ['name', 'remark'];
    const placeholder_txt: string = 'ค้นหาตามชื่อประเภทสินค้า...';

    const columns: ColumnsType<productType> = [
        {
            title: 'ลำดับ',
            dataIndex: 'key',
            key: 'key',
            width: '70px',
            align: 'center',
        },
        { 
            title: 'ชื่อประเภทสินค้า', 
            dataIndex: 'name', 
            key: 'name',
            render: (text) => (
                <div style={{display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600}}>
                    <span style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '8px',
                        background: 'var(--primary-light, #FFF3EE)',
                        color: 'var(--primary, #FF7A59)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px'
                    }}>
                        🏷️
                    </span>
                    <span>{text}</span>
                </div>
            )
        },
        { 
            title: 'จำนวนสินค้า', 
            dataIndex: 'products_count', 
            key: 'products_count',
            width: '140px',
            align: 'center',
            render: (count) => (
                <span style={{
                    background: 'var(--bg-subtle, #F1F5F9)',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontWeight: 700,
                    fontSize: '13.5px',
                    color: 'var(--text-primary, #1E293B)',
                    fontFamily: 'Nunito, sans-serif'
                }}>
                    {count || 0} รายการ
                </span>
            )
        },
        { 
            title: 'คำอธิบาย / หมายเหตุ', 
            dataIndex: 'remark', 
            key: 'remark',
            render: (text) => text || <span style={{color: '#94A3B8'}}>-</span>
        },
        { 
            title: 'วันที่สร้าง', 
            dataIndex: 'create_date', 
            key: 'create_date',
            width: '160px',
            render: (text) => text || <span style={{color: '#94A3B8'}}>-</span>
        },
        {
            title: 'สถานะ',
            dataIndex: 'status',
            key: 'status',
            width: '130px',
            align: 'center',
            render: (status) => (
                <span style={{
                    display: 'inline-block',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '12.5px',
                    fontWeight: 700,
                    background: status === 1 ? '#ECFDF5' : '#F1F5F9',
                    color: status === 1 ? '#059669' : '#64748B',
                    border: `1px solid ${status === 1 ? '#A7F3D0' : '#E2E8F0'}`
                }}>
                    {status === 1 ? '🟢 ใช้งานอยู่' : '⚪ ปิดใช้งาน'}
                </span>
            )
        },
        { 
            title: '', 
            dataIndex: 'operation', 
            width: '160px', 
            fixed: 'right', 
            render: (_, record) =>
                data.length >= 1 ? ( 
                    <div className='iconOperation'>
                        <FontAwesomeIcon icon="eye" title="ดูรายละเอียด" onClick={() => { onShowDetail('VIEW', record); }} />
                        <FontAwesomeIcon icon="copy" title="คัดลอก" onClick={() => { onShowDetail('COPY', record); }} />
                        <FontAwesomeIcon icon="pen-to-square" title="แก้ไข" onClick={() => { onShowDetail('EDIT', record); }} />
                        <FontAwesomeIcon icon="trash-can" title="ลบ" onClick={() => { onDelete(record.id, record.products_count); }} />
                    </div>
                ) : null
        }
    ];

    const init = async () => {
        let getProductType = await api_product_type('LIST', {});
        setData(getProductType || []);
    };

    const getConfigModal = (titleStr: string, contentStr: string) => {
        return {
            title: titleStr,
            icon: <ExclamationCircleFilled rev={undefined} />,
            content: contentStr,
            okText: 'ตกลง',
            cancelText: 'ยกเลิก',
            centered: true
        };
    };

    const onShowDetail = (m: string, itemData?: productType) => {
        if (m === 'ADD') {
            setTitle('เพิ่มประเภทสินค้า');
            setFormData({...initProductType});
        } else if (m === 'COPY') {
            setTitle('คัดลอกประเภทสินค้า');
            setFormData({...(itemData || initProductType), id: 0, key: 0, name: `${itemData?.name || ''} (คัดลอก)`});
        } else if (m === 'EDIT') {
            setTitle('แก้ไขประเภทสินค้า');
            setFormData(itemData ? {...itemData} : {...initProductType});
        } else if (m === 'VIEW') {
            setTitle('รายละเอียดประเภทสินค้า');
            setFormData(itemData ? {...itemData} : {...initProductType});
        }

        setMode(m);
        setOpen(true);
    };

    const onCloseDetail = () => {
        setOpen(false);
    };

    const onSave = async () => {
        let operation = 'INSERT';
        let key_id = undefined;
        let body = JSON.parse(JSON.stringify(formData));

        if (formData.id > 0 && mode === 'EDIT') {
            operation = 'UPDATE';
            key_id = formData.id;
        }

        if (!isEmpty(body['name'])) {
            let res = await api_product_type(operation, body, key_id);
            if (res === 1) {
                setOpen(false);
                Modal.success({
                    ...getConfigModal('สำเร็จ', operation === 'UPDATE' ? 'แก้ไขข้อมูลสำเร็จแล้ว' : 'เพิ่มข้อมูลสำเร็จแล้ว'),
                    onOk() { init(); }
                });
            } else {
                Modal.error({
                    ...getConfigModal('ผิดพลาด', operation === 'UPDATE' ? 'แก้ไขข้อมูลไม่สำเร็จ' : 'เพิ่มข้อมูลไม่สำเร็จ'),
                });
            }
        } else {
            Modal.warning({
                ...getConfigModal('แจ้งเตือน', 'กรุณากรอกชื่อประเภทสินค้า'),
            });
        }
    };

    const onDelete = async (key_id: number, count?: number) => {
        if (count && count > 0) {
            Modal.warning({
                ...getConfigModal('ไม่สามารถลบได้', `ประเภทสินค้านี้มีสินค้าผูกอยู่ ${count} รายการ กรุณาย้ายหรือลบสินค้าก่อน`),
            });
            return;
        }

        Modal.confirm({
            ...getConfigModal('ยืนยันการลบ', 'คุณต้องการลบประเภทสินค้านี้หรือไม่ ?'),
            async onOk() {
                let res = await api_product_type('DELETE', {}, key_id);
                if (res === 1) {
                    Modal.success({
                        ...getConfigModal('สำเร็จ', 'ลบข้อมูลสำเร็จแล้ว'),
                        onOk() { init(); }
                    });
                } else {
                    Modal.error({
                        ...getConfigModal('ผิดพลาด', 'ลบข้อมูลไม่สำเร็จ'),
                    });
                }
            }
        });
    };

    const onDataDetailChange = (payload: any) => {
        setFormData(payload);
    };

    useEffect(() => {
        init();
    }, []);

    const totalActive = data.filter(item => item.status === 1).length;

    return (
        <>
            <div className='pageHeaderBanner'>
                <div className='pageHeaderTitle'>
                    <h2>🏷️ จัดการประเภทสินค้า (Product Categories)</h2>
                    <span className='pageHeaderSubtitle'>กำหนดหมวดหมู่ของสินค้าเพื่อความสะดวกในการค้นหา จัดกลุ่ม และคิดราคาสินค้า</span>
                </div>
                <div className='headerStatsGrid'>
                    <div className='headerStatCard'>
                        <span className='statLabel'>ประเภททั้งหมด</span>
                        <span className='statValue'>{data.length} หมวด</span>
                    </div>
                    <div className='headerStatCard highlight'>
                        <span className='statLabel'>ใช้งานอยู่</span>
                        <span className='statValue highlight'>{totalActive} หมวด</span>
                    </div>
                </div>
            </div>

            <CommonTableList 
                page="product_type"
                placeholder={placeholder_txt} 
                columns={columns} 
                data={[...data]} 
                filterField={filterField} 
                onAdd={() => { onShowDetail('ADD'); }} 
            />

            <CommonDetail 
                width='420px'
                mode={mode} 
                title={title} 
                open={open} 
                onClosed={onCloseDetail} 
                onSaved={onSave}
                element={
                    <ProductTypeDetail 
                        disabled={mode === 'VIEW'}
                        productType={{...formData}} 
                        onChange={onDataDetailChange} 
                    />
                }
            />
        </>
    );
};

export default ProductType;
